import {
  NumberInput,
  Select,
  Checkbox,
  Stack,
  Alert,
  Paper,
  Text,
  Fieldset,
  Divider,
} from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { isDev } from '@/hooks/is-dev';
import { IconInfoCircle } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { FileUpload } from '@/components/data-input/file-upload';
import type { ParsedData, Loan } from '@/types/nsldsTypes';
import { US_STATES, DEBUG_TEST_CASES } from './survey-constants';
import { formatCurrency, formatPercentage } from '@/utils/formatters';

export interface UserSurveyData {
  annualIncome: number | null;
  includePartner: boolean;
  partnerAnnualIncome: number | null;
  familySize: number | null;
  state: string | null;
  pslfEligible: boolean;
  pslfEligiblePartner: boolean;
  appointmentMade: boolean;
  filingStatus: 'Single' | 'MFJ' | 'MFS' | null;
}

// const LOAN_TYPE_GROUP_OPTIONS = [
//   { value: 'Direct', label: 'Direct' },
//   { value: 'FFEL', label: 'FFEL' },
//   { value: 'Perkins', label: 'Perkins' },
//   { value: 'Direct Plus Parent', label: 'Direct Plus Parent' },
//   { value: 'FFEL Plus Parent', label: 'FFEL Plus Parent' },
//   { value: 'Direct Plus Consolidation', label: 'Direct Plus Consolidation' },
// ];

interface UserSurveyProps {
  form: UseFormReturnType<UserSurveyData>;
  userFile: File | null;
  setUserFile: (file: File | null) => void;
  partnerFile: File | null;
  setPartnerFile: (file: File | null) => void;
  userManualLoan: Loan | null;
  setUserManualLoan: (loan: Loan | null) => void;
  partnerManualLoan: Loan | null;
  setPartnerManualLoan: (loan: Loan | null) => void;
  isLoadingUserFile: boolean;
  isLoadingPartnerFile: boolean;
  userFileError: string | null;
  setUserFileError: (error: string | null) => void;
  partnerFileError: string | null;
  setPartnerFileError: (error: string | null) => void;
  setUserParsedData: (data: ParsedData | null) => void;
  setPartnerParsedData: (data: ParsedData | null) => void;
  userParsedData: ParsedData | null;
  partnerParsedData: ParsedData | null;
}

export function UserSurvey({
  form,
  userFile,
  setUserFile,
  partnerFile,
  setPartnerFile,
  userManualLoan,
  setUserManualLoan,
  partnerManualLoan,
  setPartnerManualLoan,
  isLoadingUserFile,
  isLoadingPartnerFile,
  userFileError,
  setUserFileError,
  partnerFileError,
  setPartnerFileError,
  setUserParsedData,
  setPartnerParsedData,
  userParsedData,
  partnerParsedData,
}: UserSurveyProps) {
  const [userFileHasNoBalanceWarning, setUserFileHasNoBalanceWarning] = useState<string | null>(
    null
  );
  const [partnerFileHasNoBalanceWarning, setPartnerFileHasNoBalanceWarning] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (userFile && userParsedData) {
      const loans = userParsedData.loansData;
      if (loans.length === 0) {
        setUserFileHasNoBalanceWarning(
          'Your NSLDS file was processed, but it contains no loan data.'
        );
      } else if (loans.every((l) => l['Total Outstanding'] === 0)) {
        setUserFileHasNoBalanceWarning(
          'Your NSLDS file was processed, but it contains no loans with an outstanding balance.'
        );
      } else {
        setUserFileHasNoBalanceWarning(null); // Has loans with balances
      }
    } else if (!userFile) {
      setUserFileHasNoBalanceWarning(null); // Clear warning if no file is present
    }
  }, [userFile, userParsedData, setUserFileHasNoBalanceWarning]);

  useEffect(() => {
    if (partnerFile && partnerParsedData && form.values.includePartner) {
      const loans = partnerParsedData.loansData;
      if (loans.length === 0) {
        setPartnerFileHasNoBalanceWarning(
          "Partner's NSLDS file was processed, but it contains no loan data."
        );
      } else if (loans.every((l) => l['Total Outstanding'] === 0)) {
        setPartnerFileHasNoBalanceWarning(
          "Partner's NSLDS file was processed, but it contains no loans with an outstanding balance."
        );
      } else {
        setPartnerFileHasNoBalanceWarning(null); // Has loans with balances
      }
    } else if (!partnerFile || !form.values.includePartner) {
      setPartnerFileHasNoBalanceWarning(null); // Clear warning if no partner file or partner not included
    }
  }, [
    partnerFile,
    partnerParsedData,
    form.values.includePartner,
    setPartnerFileHasNoBalanceWarning,
  ]);

  const handleDebugSelectChange = (value: string | null) => {
    const selectedCase = DEBUG_TEST_CASES.find((c) => c.label === value);
    if (selectedCase) {
      form.setValues(selectedCase.value);
      if (selectedCase.userManualLoanData) {
        setUserManualLoan(selectedCase.userManualLoanData);
        setUserFile(null);
        setUserParsedData(null);
      }
      if (selectedCase.partnerManualLoanData && form.values.includePartner) {
        setPartnerManualLoan(selectedCase.partnerManualLoanData);
        setPartnerFile(null);
        setPartnerParsedData(null);
      }
    }
  };

  const partnerLabel = 'Partner';

  useEffect(() => {
    if (!form.values.includePartner) {
      if (form.values.pslfEligiblePartner) form.setFieldValue('pslfEligiblePartner', false);
      if (partnerFile) setPartnerFile(null);
      if (partnerParsedData) setPartnerParsedData(null);
      if (partnerFileError) setPartnerFileError(null);
    }
  }, [
    form.values.includePartner,
    form.values.familySize,
    form.values.pslfEligiblePartner,
    partnerFile,
    partnerParsedData,
    partnerFileError,
    setPartnerFile,
    setPartnerParsedData,
    setPartnerFileError,
    form.setFieldValue,
    form,
  ]);

  const isDevelopment = isDev();

  const calculateLoanSummary = (loans: Loan[] | undefined) => {
    if (!loans || loans.length === 0) {
      return { totalBalance: 0, averageInterestRate: 0, loanCount: 0 };
    }
    const loansWithBalance = loans.filter((loan) => (loan['Total Outstanding'] || 0) > 0);

    if (loansWithBalance.length === 0) {
      return { totalBalance: 0, averageInterestRate: 0, loanCount: 0 };
    }

    const totalBalance = loansWithBalance.reduce(
      (acc, loan) => acc + (loan['Total Outstanding'] || 0),
      0
    );
    const totalPrincipalForWeightedAverage = loansWithBalance.reduce(
      (acc, loan) => acc + (loan['Outstanding Principal'] || 0),
      0
    );
    const weightedInterestSum = loansWithBalance.reduce(
      (acc, loan) => acc + (loan['Outstanding Principal'] || 0) * (loan['Loan Interest Rate'] || 0),
      0
    );
    const averageInterestRate =
      totalPrincipalForWeightedAverage > 0
        ? weightedInterestSum / totalPrincipalForWeightedAverage
        : 0;
    return { totalBalance, averageInterestRate, loanCount: loansWithBalance.length };
  };

  const userLoanSummary = calculateLoanSummary(userParsedData?.loansData);
  const partnerLoanSummary = calculateLoanSummary(partnerParsedData?.loansData);

  const renderLoanInputs = (
    borrowerType: 'user' | 'partner',
    parsedData: ParsedData | null,
    manualLoan: Loan | null,
    setManualLoan: (loan: Loan | null) => void,
    fileHasNoBalanceWarning: string | null,
    setFileHasNoBalanceWarning: (warning: string | null) => void,
    loanSummary: ReturnType<typeof calculateLoanSummary>,
    currentFile: File | null,
    setCurrentFile: (file: File | null) => void,
    setBorrowerParsedData: (data: ParsedData | null) => void,
    setBorrowerFileError: (error: string | null) => void,
    isLoadingFile: boolean
  ) => {
    const borrowerKey = borrowerType === 'user' ? 'user' : 'partner';
    const defaultManualLoanId =
      borrowerType === 'user' ? 'USER_MANUAL_LOAN' : 'PARTNER_MANUAL_LOAN';
    const manualLoanName = borrowerType === 'user' ? 'Manual Loan' : 'Partner Manual Loan';

    const showManualInputsAndOrText = !(parsedData && parsedData.loansData.length > 0);

    return (
      <Stack my="xs" gap="sm">
        <Stack gap="xs">
          {parsedData && parsedData.loansData.length > 0 && (
            <Stack gap={0} mb="xs">
              <Text size="sm" fw={500}>
                {loanSummary.loanCount} loan{loanSummary.loanCount > 1 ? 's' : ''} from NSLDS file:
              </Text>
              <Text size="sm">Total balance: {formatCurrency(loanSummary.totalBalance)}</Text>
              <Text size="sm">
                Avg. interest rate: {formatPercentage(loanSummary.averageInterestRate, 2)}
              </Text>
            </Stack>
          )}

          {fileHasNoBalanceWarning && (
            <Alert
              title="Issue reading NSLDS file"
              color="yellow"
              icon={<IconInfoCircle />}
              withCloseButton
              onClose={() => {
                setFileHasNoBalanceWarning(null);
              }}
              mb="xs"
            >
              {fileHasNoBalanceWarning}
            </Alert>
          )}

          <FileUpload
            file={currentFile}
            setFile={(newFile) => {
              setCurrentFile(newFile);
              if (!newFile) {
                setBorrowerParsedData(null);
                setBorrowerFileError(null);
                if (!manualLoan || manualLoan['Total Outstanding'] === 0) {
                  setManualLoan({
                    'Loan Award ID': defaultManualLoanId,
                    Borrower: borrowerKey,
                    'Total Outstanding': 0,
                    'Outstanding Principal': 0,
                    'Outstanding Interest': 0,
                    'Loan Interest Rate': 0,
                    'Loan Type Group': 'Direct',
                    'Current Loan Status Description': 'In Repayment',
                    'Loan PSLF Cumulative Matched Months': 0,
                    qualifiedPaymentMonths: 0,
                    isInRepayment: true,
                    originalPrincipal: 0,
                    degreeType: 'graduate',
                    isPartnerLoan: borrowerType === 'partner',
                    'Loan Name': manualLoanName,
                  } as Loan);
                }
              }
            }}
            fileError={borrowerType === 'user' ? userFileError : partnerFileError}
            borrowerType={borrowerType}
            isLoading={isLoadingFile}
          />
        </Stack>

        {showManualInputsAndOrText && (
          <Divider
            label="or"
            labelPosition="center"
            px="lg"
            styles={{ label: { fontStyle: 'italic' } }}
          />
        )}

        {showManualInputsAndOrText && (
          <Stack gap="xs">
            <NumberInput
              label="Total balance"
              placeholder="Enter total principal & interest"
              leftSection="$"
              allowDecimal
              allowNegative={false}
              hideControls
              thousandSeparator=","
              size="sm"
              value={manualLoan?.['Total Outstanding'] || ''}
              onChange={(value) => {
                const totalOutstanding = Number(value) || 0;
                setManualLoan({
                  ...(manualLoan || {}),
                  'Loan Award ID': manualLoan?.['Loan Award ID'] || defaultManualLoanId,
                  Borrower: borrowerKey,
                  'Total Outstanding': totalOutstanding,
                  'Outstanding Principal': totalOutstanding,
                  'Outstanding Interest': 0,
                  'Loan Interest Rate': manualLoan?.['Loan Interest Rate'] || 0,
                  'Loan Type Group': manualLoan?.['Loan Type Group'] || 'Direct',
                  'Current Loan Status Description':
                    manualLoan?.['Current Loan Status Description'] || 'In Repayment',
                  'Loan PSLF Cumulative Matched Months':
                    manualLoan?.['Loan PSLF Cumulative Matched Months'] || 0,
                  qualifiedPaymentMonths: manualLoan?.qualifiedPaymentMonths || 0,
                  isInRepayment: manualLoan?.isInRepayment ?? true,
                  originalPrincipal: manualLoan?.originalPrincipal || totalOutstanding,
                  degreeType: manualLoan?.degreeType || 'graduate',
                  isPartnerLoan: borrowerType === 'partner',
                  'Loan Name': manualLoan?.['Loan Name'] || manualLoanName,
                } as Loan);
              }}
            />
            <NumberInput
              label="Average interest rate"
              placeholder="Enter average rate (e.g., 6.8)"
              rightSection="%"
              allowDecimal
              decimalScale={3}
              allowNegative={false}
              hideControls
              size="sm"
              value={
                manualLoan?.['Loan Interest Rate']
                  ? (manualLoan['Loan Interest Rate'] * 100).toFixed(3).replace(/\.?0+$/, '')
                  : ''
              }
              onChange={(value) => {
                const interestRate = (Number(value) || 0) / 100;
                setManualLoan({
                  ...(manualLoan || {}),
                  'Loan Award ID': manualLoan?.['Loan Award ID'] || defaultManualLoanId,
                  Borrower: borrowerKey,
                  'Loan Interest Rate': interestRate,
                  'Total Outstanding': manualLoan?.['Total Outstanding'] || 0,
                  'Outstanding Principal':
                    manualLoan?.['Outstanding Principal'] || manualLoan?.['Total Outstanding'] || 0,
                  'Outstanding Interest': manualLoan?.['Outstanding Interest'] || 0,
                  'Loan Type Group': manualLoan?.['Loan Type Group'] || 'Direct',
                  'Current Loan Status Description':
                    manualLoan?.['Current Loan Status Description'] || 'In Repayment',
                  'Loan PSLF Cumulative Matched Months':
                    manualLoan?.['Loan PSLF Cumulative Matched Months'] || 0,
                  qualifiedPaymentMonths: manualLoan?.qualifiedPaymentMonths || 0,
                  isInRepayment: manualLoan?.isInRepayment ?? true,
                  originalPrincipal:
                    manualLoan?.originalPrincipal || manualLoan?.['Total Outstanding'] || 0,
                  degreeType: manualLoan?.degreeType || 'graduate',
                  isPartnerLoan: borrowerType === 'partner',
                  'Loan Name': manualLoan?.['Loan Name'] || manualLoanName,
                } as Loan);
              }}
            />
            <NumberInput
              label="Qualified payment months"
              placeholder="Enter in months"
              description="Number of qualifying payments made towards forgiveness programs"
              allowDecimal={false}
              allowNegative={false}
              hideControls
              size="sm"
              min={0}
              max={360}
              value={
                borrowerType === 'user' && form.values.pslfEligible
                  ? manualLoan?.['Loan PSLF Cumulative Matched Months'] || ''
                  : borrowerType === 'partner' && form.values.pslfEligiblePartner
                  ? manualLoan?.['Loan PSLF Cumulative Matched Months'] || ''
                  : manualLoan?.qualifiedPaymentMonths || ''
              }
              onChange={(value) => {
                const qualifiedPaymentMonths = Number(value) || 0;
                const isPslfEnabled =
                  borrowerType === 'user'
                    ? form.values.pslfEligible
                    : form.values.pslfEligiblePartner;

                setManualLoan({
                  ...(manualLoan || {}),
                  'Loan Award ID': manualLoan?.['Loan Award ID'] || defaultManualLoanId,
                  Borrower: borrowerKey,
                  // Set the appropriate field based on PSLF eligibility
                  qualifiedPaymentMonths: isPslfEnabled ? 0 : qualifiedPaymentMonths,
                  'Loan PSLF Cumulative Matched Months': isPslfEnabled ? qualifiedPaymentMonths : 0,
                  'Total Outstanding': manualLoan?.['Total Outstanding'] || 0,
                  'Outstanding Principal':
                    manualLoan?.['Outstanding Principal'] || manualLoan?.['Total Outstanding'] || 0,
                  'Outstanding Interest': manualLoan?.['Outstanding Interest'] || 0,
                  'Loan Interest Rate': manualLoan?.['Loan Interest Rate'] || 0,
                  'Loan Type Group': manualLoan?.['Loan Type Group'] || 'Direct',
                  'Current Loan Status Description':
                    manualLoan?.['Current Loan Status Description'] || 'In Repayment',
                  isInRepayment: manualLoan?.isInRepayment ?? true,
                  originalPrincipal:
                    manualLoan?.originalPrincipal || manualLoan?.['Total Outstanding'] || 0,
                  degreeType: manualLoan?.degreeType || 'graduate',
                  isPartnerLoan: borrowerType === 'partner',
                  'Loan Name': manualLoan?.['Loan Name'] || manualLoanName,
                } as Loan);
              }}
            />
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <Paper>
      {isDevelopment && (
        <Select
          label="Debug test cases"
          placeholder="Select test case"
          data={DEBUG_TEST_CASES.map((c) => c.label)}
          onChange={handleDebugSelectChange}
          clearable
          style={{
            position: 'fixed',
            bottom: 10,
            right: 10,
            zIndex: 9999,
            width: 200,
            border: '2px solid var(--mantine-color-blue-5)',
          }}
          size="xs"
          comboboxProps={{ shadow: 'md', transitionProps: { transition: 'pop', duration: 200 } }}
        />
      )}
      <Stack gap="xl">
        <Fieldset legend="About you">
          <Stack gap="sm">
            <Select
              label="State of residence"
              placeholder="Select your state"
              data={US_STATES}
              searchable
              withAsterisk
              allowDeselect={false}
              size="sm"
              {...form.getInputProps('state')}
            />{' '}
            <Checkbox
              label="I am married"
              description="This will allow you to add your partner's income and loans."
              size="sm"
              {...form.getInputProps('includePartner', { type: 'checkbox' })}
            />
            <NumberInput
              label="Family size"
              min={1}
              max={20}
              allowDecimal={false}
              allowNegative={false}
              hideControls
              withAsterisk
              size="sm"
              {...form.getInputProps('familySize')}
            />
            <NumberInput
              label="Annual income"
              placeholder="Your total annual income"
              leftSection="$"
              allowDecimal={false}
              allowNegative={false}
              hideControls
              withAsterisk
              thousandSeparator=","
              size="sm"
              {...form.getInputProps('annualIncome')}
            />
            <Checkbox
              label="I am pursuing public service loan forgiveness (PSLF)"
              size="sm"
              {...form.getInputProps('pslfEligible', { type: 'checkbox' })}
            />
            {form.values.includePartner && (
              <Select
                label="Tax filing status"
                data={[
                  { value: 'MFJ', label: 'Married filing jointly' },
                  { value: 'MFS', label: 'Married filing separately' },
                ]}
                placeholder="Select filing status"
                size="sm"
                withAsterisk
                {...form.getInputProps('filingStatus')}
              />
            )}
          </Stack>
        </Fieldset>

        <Fieldset legend="Your loans">
          {renderLoanInputs(
            'user',
            userParsedData,
            userManualLoan,
            setUserManualLoan,
            userFileHasNoBalanceWarning,
            setUserFileHasNoBalanceWarning,
            userLoanSummary,
            userFile,
            setUserFile,
            setUserParsedData,
            setUserFileError,
            isLoadingUserFile
          )}
        </Fieldset>

        {form.values.includePartner && (
          <>
            <Fieldset legend="Partner's information">
              <Stack gap="sm">
                <NumberInput
                  label={`${partnerLabel}'s annual income`}
                  placeholder={`${partnerLabel}'s total annual income`}
                  leftSection="$"
                  allowDecimal={false}
                  allowNegative={false}
                  hideControls
                  withAsterisk
                  thousandSeparator=","
                  size="sm"
                  {...form.getInputProps('partnerAnnualIncome')}
                />
                <Checkbox
                  label={`My ${partnerLabel.toLowerCase()} is pursuing public service loan forgiveness (PSLF)`}
                  size="sm"
                  {...form.getInputProps('pslfEligiblePartner', { type: 'checkbox' })}
                />
              </Stack>
            </Fieldset>

            <Fieldset legend={`${partnerLabel}'s loans`}>
              {renderLoanInputs(
                'partner',
                partnerParsedData,
                partnerManualLoan,
                setPartnerManualLoan,
                partnerFileHasNoBalanceWarning,
                setPartnerFileHasNoBalanceWarning,
                partnerLoanSummary,
                partnerFile,
                setPartnerFile,
                setPartnerParsedData,
                setPartnerFileError,
                isLoadingPartnerFile
              )}
            </Fieldset>
          </>
        )}
      </Stack>
    </Paper>
  );
}
